import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LockScreenService } from './lock-screen.service';
import { TIMEOUTS } from '../constants/timeouts.constants';
import { environment } from '@/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class InactivityService implements OnDestroy {
    private readonly INACTIVITY_TIMEOUT = TIMEOUTS.INACTIVITY;
    private destroy$ = new Subject<void>();
    private userActivity: Subscription | null = null;
    private inactivityTimer: Subscription | null = null;
    private lastActivityTime: number = Date.now();

    constructor(private lockScreenService: LockScreenService) {}

    /** Starts (or restarts) inactivity tracking. Safe to call multiple times. */
    startWatching(): void {
        this.stopWatching();
        this.lastActivityTime = Date.now();
        this.setupActivityListeners();
        this.startInactivityTimer();
    }

    /** Subscribes to user input events and refreshes the last-activity timestamp. */
    private setupActivityListeners(): void {
        const activity$ = merge(
            fromEvent(document, 'mousemove'),
            fromEvent(document, 'mousedown'),
            fromEvent(document, 'mouseup'),
            fromEvent(document, 'click'),
            fromEvent(document, 'keydown'),
            fromEvent(document, 'keyup'),
            fromEvent(document, 'touchstart'),
            fromEvent(document, 'touchend'),
            fromEvent(document, 'scroll'),
            fromEvent(document, 'wheel')
        ).pipe(debounceTime(100), takeUntil(this.destroy$));

        this.userActivity = activity$.subscribe(() => {
            this.lastActivityTime = Date.now();
        });
    }

    /** Polls every 500ms and locks the screen after the inactivity timeout (production only). */
    private startInactivityTimer(): void {
        this.inactivityTimer = timer(0, 500)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const currentTime = Date.now();
                const timeSinceLastActivity = currentTime - this.lastActivityTime;

                if (timeSinceLastActivity >= this.INACTIVITY_TIMEOUT && environment.production) {
                    this.lockScreenService.lock();
                }
            });
    }

    /** Tears down activity listeners and the inactivity timer. */
    stopWatching(): void {
        if (this.inactivityTimer) {
            this.inactivityTimer.unsubscribe();
            this.inactivityTimer = null;
        }
        if (this.userActivity) {
            this.userActivity.unsubscribe();
            this.userActivity = null;
        }
    }

    /** Cleans up all subscriptions when the service is destroyed. */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopWatching();
    }
}
